import axios from 'axios';

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

class ApiService {
  constructor() {
    this.client = axios.create({
      baseURL: BASE_URL,
      timeout: 30000,
    });

    // Request interceptor to add authentication headers
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('authToken');

        if (token) {
          config.headers['Authorization'] = `Bearer ${token}`;
        }

        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('merchantData');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Authentication
  async login(identifier, password) {
    try {
      const response = await this.client.post('/merchant/auth/login', {
        identifier,
        password
      });

      if (response.data.success) {
        const { token, merchant } = response.data.data;
        localStorage.setItem('authToken', token);
        localStorage.setItem('merchantData', JSON.stringify(merchant));
        return response.data;
      }
    } catch (error) {
      throw new Error(error.response?.data?.error?.message || 'Authentication failed');
    }
  }

  logout() {
    localStorage.removeItem('authToken');
    localStorage.removeItem('merchantData');
  }

  // Merchant Profile
  async getMerchantProfile() {
    const response = await this.client.get('/merchant/profile');
    return response.data;
  }

  async updateMerchantProfile(data) {
    const response = await this.client.put('/merchant/profile', data);
    return response.data;
  }

  // Dashboard
  async getDashboardSummary(params = {}) {
    const response = await this.client.get('/merchant/dashboard/summary', { params });
    return response.data;
  }

  // Payin Transactions
  async createPayin(data) {
    const response = await this.client.post('/payin/create', data);
    return response.data;
  }

  async getPayinStatus(transactionId) {
    const response = await this.client.get(`/payin/status/${transactionId}`);
    return response.data;
  }

  async getPayinList(params = {}) {
    const response = await this.client.get('/payin/list', { params });
    return response.data;
  }

  async getPayinSummary(params = {}) {
    const response = await this.client.get('/payin/summary', { params });
    return response.data;
  }

  async cancelPayin(transactionId) {
    const response = await this.client.post(`/payin/cancel/${transactionId}`);
    return response.data;
  }

  // Payout Transactions
  async initiatePayout(data) {
    const response = await this.client.post('/payout/initiate', data);
    return response.data;
  }

  async getPayoutStatus(payoutId) {
    const response = await this.client.get(`/payout/status/${payoutId}`);
    return response.data;
  }

  async getPayoutList(params = {}) {
    const response = await this.client.get('/payout/list', { params });
    return response.data;
  }

  async getPayoutSummary(params = {}) {
    const response = await this.client.get('/payout/summary', { params });
    return response.data;
  }

  // Wallet
  async getWalletBalance() {
    const response = await this.client.get('/wallet/balance');
    return response.data;
  }

  async getWalletTransactions(params = {}) {
    const response = await this.client.get('/wallet/transactions', { params });
    return response.data;
  }

  async requestWalletTopup(formData) {
    const response = await this.client.post('/wallet/topup/request', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async getTopupRequests(params = {}) {
    const response = await this.client.get('/wallet/topup/list', { params });
    return response.data;
  }

  async getTopupRequest(requestId) {
    const response = await this.client.get(`/wallet/topup/${requestId}`);
    return response.data;
  }

  // API Keys
  async getApiKeys() {
    const response = await this.client.get('/merchant/api-keys');
    return response.data;
  }

  async regenerateApiSecret() {
    const response = await this.client.post('/merchant/api-keys/regenerate', {}, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  }

  // KYC
  async submitKyc(formData) {
    const response = await this.client.post('/merchant/kyc/submit', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async getKycStatus() {
    const response = await this.client.get('/merchant/kyc/status');
    return response.data;
  }

  // Analytics
  async getAnalytics(params = {}) {
    const response = await this.client.get('/merchant/analytics', { params });
    return response.data;
  }

  // Reports
  async getTransactionReport(params = {}) {
    // For CSV format, we need to specify responseType as text to get raw CSV data
    const config = {
      params,
      responseType: params.format === 'csv' ? 'text' : 'json'
    };

    const response = await this.client.get('/merchant/transactions/report', config);
    return response.data;
  }

  // Webhooks
  async setWebhookUrl(data) {
    const response = await this.client.post('/merchant/webhook/set', data);
    return response.data;
  }
}

export default new ApiService();